import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { TimePicker } from '../../components/ui/time-picker';
import { bookTable, freeTable } from '../../services/tableService';
import { floorPlanService } from '../../services/floorPlanService';
import orderService from '../../services/orderService';
import crmService from '../../services/crmService';
import apiClient from '../../services/api';
import { buildBill, buildBillHtml } from '../../lib/billPrint';
import { detectBridge, listPrinters, printHtml } from '../../lib/printBridge';
import BillRewardsPanel from './BillRewardsPanel';
import {
  Users, Clock, X, CheckCircle, MapPin, LayoutGrid, User, Phone, Mail, ExternalLink,
  Receipt, Printer,
} from 'lucide-react';
import { useAppSettings } from '../../context/AppSettingsContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { alertDialog, confirm } from '../../components/ui/confirm-dialog';
import { subscribeRealtime, fallbackPoll } from '../../lib/realtime';

// Order- and item-status badge palettes for the live detail on occupied cards.
const ORDER_STATUS_COLORS = {
  PENDING: 'bg-amber-100 text-amber-800',
  ORDERED: 'bg-amber-100 text-amber-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  PREPARING: 'bg-orange-100 text-orange-800',
  READY: 'bg-green-100 text-green-800',
  // Served but the bill still open — the table stays occupied until it settles.
  DELIVERED: 'bg-emerald-100 text-emerald-800',
};
const ITEM_STATUS_COLORS = {
  PENDING: 'text-amber-600',
  PREPARING: 'text-orange-600',
  READY: 'text-green-600',
  SERVED: 'text-gray-400',
};

/* Per-LINE service clock. Rounds land on one sitting order at different
   times and each dish is served on its own, so ordered → served (and the
   minutes between) is printed per item, not per order. */
const itemClockTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const h = d.getHours();
  return `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')} ${h >= 12 ? 'pm' : 'am'}`;
};
const itemMinutes = (fromIso, toIso) => {
  const a = new Date(fromIso).getTime();
  const b = toIso ? new Date(toIso).getTime() : Date.now();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.max(0, Math.round((b - a) / 60000));
};
const itemClockLine = (it) => {
  if (!it?.ordered_at) return null;
  if (it.served_at) {
    const m = itemMinutes(it.ordered_at, it.served_at);
    return `ordered ${itemClockTime(it.ordered_at)} → served ${itemClockTime(it.served_at)}${m != null ? ` · ${m}m` : ''}`;
  }
  if (it.status === 'SERVED') {
    // Served before the per-line clock existed: the moment was never recorded.
    return `ordered ${itemClockTime(it.ordered_at)} → served (time not recorded)`;
  }
  if (it.status === 'HELD') return null; // not fired yet — the badge says it
  const m = itemMinutes(it.ordered_at, null);
  // Unserved lines say WHERE the dish is, not just how long: a plate sitting
  // at the pass is a different problem from one still on the fire.
  const state =
    it.status === 'READY' ? 'ready at the pass'
    : it.status === 'PREPARING' ? 'cooking'
    : 'waiting';
  return `ordered ${itemClockTime(it.ordered_at)}${m != null ? ` · ${state} ${m}m` : ''}`;
};

// Human-readable message from whatever a service call threw: our API's
// {error}, DRF's {detail} (auth/permission errors), an Error, or a string —
// never "[object Object]".
const errText = (err) =>
  err?.error || err?.detail || err?.message ||
  (typeof err === 'string' ? err : 'Something went wrong. Please try again.');

const TablesViewPage = () => {
  const { formatDateTime: fmtDateTime, formatCurrency, formatDate } = useAppSettings();
  const { restaurantId, locationId, location, restaurant } = useRestaurant();
  const navigate = useNavigate();

  // Flat list of tables enriched with floor/section labels + live status.
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Self-service reservation hold time (minutes) — how long a guest's QR
  // table check-in holds the table before it auto-frees.
  const [reserveMinutes, setReserveMinutes] = useState(60);
  const [reserveSaving, setReserveSaving] = useState(false);
  const [reserveSaved, setReserveSaved] = useState(false);

  useEffect(() => {
    if (!restaurantId) return;
    apiClient.get(`/users/restaurant/${restaurantId}/table-settings/`)
      .then(({ data }) => {
        const m = data?.data?.table_reserve_minutes;
        if (m != null) setReserveMinutes(m);
      })
      .catch(() => {});
  }, [restaurantId]);

  const saveReserveMinutes = async () => {
    setReserveSaving(true);
    setReserveSaved(false);
    try {
      const { data } = await apiClient.patch(
        `/users/restaurant/${restaurantId}/table-settings/`,
        { table_reserve_minutes: Number(reserveMinutes) },
      );
      if (data?.data?.table_reserve_minutes != null) setReserveMinutes(data.data.table_reserve_minutes);
      setReserveSaved(true);
      setTimeout(() => setReserveSaved(false), 2500);
    } catch (e) {
      await alertDialog({
        title: 'Could not save',
        description: e?.response?.data?.error || 'Failed to update the reservation time.',
      });
    } finally {
      setReserveSaving(false);
    }
  };

  // Capacity filter for the table grid.
  const [minCapacity, setMinCapacity] = useState('');

  // Table detail drawer: active orders per party + full order history.
  const [detailModal, setDetailModal] = useState(null); // {table, loading, data, error}

  // Guest card: a name on the order sheet opens who that guest is — phone,
  // email, visits, spend, no-shows — with a way through to the CRM profile.
  // {name, phone, loading, profile, error}; the seq guards against a slow
  // lookup landing on a card opened for somebody else.
  const [guestCard, setGuestCard] = useState(null);
  const guestSeq = useRef(0);

  // The bill for one served order: {order, loading, detail, taxes, error, paid}.
  const [bill, setBill] = useState(null);

  // Booking modal state
  const [bookingModal, setBookingModal] = useState({ show: false, table: null });
  const [bookingType, setBookingType] = useState('now');
  const [reservationTime, setReservationTime] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [pax, setPax] = useState('');

  // Advanced booking mode state
  const [bookingMode, setBookingMode] = useState('SINGLE');
  const [selectedTablesToJoin, setSelectedTablesToJoin] = useState([]);
  const [seatsNeeded, setSeatsNeeded] = useState('');
  const [overrideStaffId, setOverrideStaffId] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  // Load the location's floor plan (the real source of tables — same data the
  // order screen uses) and overlay live busy status from the with-orders feed.
  // Tables live under Location → Floor → Section (Room) → Table, so we flatten
  // that tree, keeping the floor/section labels on each table.
  /* opts.silent: a background repaint (push event, fallback poll) must not
     flash the full-page spinner over a board someone is reading. */
  const fetchTables = useCallback(async (opts = {}) => {
    if (!restaurantId || !locationId) {
      setTables([]);
      setLoading(false);
      return;
    }
    try {
      if (!opts.silent) setLoading(true);
      const [plan, withOrders] = await Promise.all([
        floorPlanService.get(restaurantId, locationId).catch(() => ({ floors: [] })),
        orderService.getTables(restaurantId).catch(() => []),
      ]);

      // table id → busy? (an active order, a guest's open QR check-in session,
      // or an occupied status marks it taken) + the live detail: shared flag,
      // open sessions and active orders with per-item kitchen status.
      const busy = {};
      const live = {};
      (withOrders || []).forEach((t) => {
        busy[t.id] = !!(t.has_active_order || t.has_open_session || t.occupancy_status === 'occupied');
        live[t.id] = {
          isShared: !!t.is_shared,
          sessions: t.open_sessions || [],
          orders: t.active_orders || [],
        };
      });

      const flat = [];
      (plan?.floors || []).forEach((floor) => {
        (floor.sections || []).forEach((section) => {
          (section.tables || []).forEach((t) => {
            flat.push({
              tableId: t.id,
              tableCode: t.table_code,
              capacity: t.capacity,
              tableType: t.table_type,
              notes: t.notes,
              floorId: floor.id,
              floorName: floor.name,
              floorNumber: floor.floor_number,
              sectionId: section.id,
              sectionName: section.name,
              status: busy[t.id] ? 'OCCUPIED' : 'AVAILABLE',
              isShared: live[t.id]?.isShared || false,
              sessions: live[t.id]?.sessions || [],
              orders: live[t.id]?.orders || [],
            });
          });
        });
      });

      setTables(flat);
      setError(null);
    } catch (err) {
      setError(err.error || err.message || 'Failed to fetch tables');
      console.error('Error fetching tables:', err);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, locationId]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  /* Push first, poll underneath: a phone order on a table QR broadcasts a
     'kitchen' event the moment it is written, so the board repaints in the
     same second; the poll covers a browser whose socket is down - every 30s
     while the socket is open, every 5s while it is not. */
  useEffect(() => {
    const unsub = subscribeRealtime(['kitchen'], () => fetchTables({ silent: true }));
    const stopPoll = fallbackPoll(() => fetchTables({ silent: true }), 30000);
    return () => { unsub(); stopPoll(); };
  }, [fetchTables]);

  const handleBookTable = async () => {
    try {
      const bookingData = {
        booking_type: bookingType === 'now' ? 'immediate' : 'appointment',
        booking_mode: bookingMode,
        pax: parseInt(pax) || null,
        customer_name: guestName,
        customer_phone: guestPhone,
      };

      if (bookingType === 'later' && reservationTime) {
        bookingData.reserved_start_at = new Date(reservationTime).toISOString();
      }

      if (bookingMode === 'JOIN' && selectedTablesToJoin.length > 0) {
        bookingData.additional_table_ids = selectedTablesToJoin;
      }

      if (bookingMode === 'SHARE' && seatsNeeded) {
        bookingData.seats_needed = parseInt(seatsNeeded);
      }

      if (bookingMode === 'OVERRIDE' && overrideStaffId && overrideReason) {
        bookingData.override_staff_id = parseInt(overrideStaffId);
        bookingData.override_reason = overrideReason;
      }

      await bookTable(restaurantId, bookingModal.table.tableId, bookingData);

      fetchTables();
      closeBookingModal();
    } catch (err) {
      await alertDialog({ title: 'Error', description: `Failed to book table: ${err.error || err.message || err}`, tone: 'destructive' });
    }
  };

  // Staff frees an occupied table — closes its open guest sessions. If the
  // table still carries unserved orders, staff choose whether those are
  // cancelled too or left running for the kitchen.
  const handleFreeTable = async (table) => {
    // Orders not yet served — the ones staff may want to cancel along the way.
    const unserved = (table.orders || []).filter((o) =>
      ['PENDING', 'ORDERED', 'CONFIRMED', 'PREPARING', 'READY'].includes(o.status)
    );
    const ok = await confirm({
      title: `Free table ${table.tableCode}?`,
      description:
        "This ends the guests' sitting and makes the table available again." +
        (unserved.length
          ? ` The table still has ${unserved.length} unserved order${unserved.length === 1 ? '' : 's'} — you'll choose next whether to cancel ${unserved.length === 1 ? 'it' : 'them'}.`
          : ''),
      confirmText: 'Free table',
      tone: 'destructive',
    });
    if (!ok) return;
    let cancelOrders = false;
    if (unserved.length) {
      cancelOrders = await confirm({
        title: `Cancel ${unserved.length === 1 ? 'the unserved order' : `all ${unserved.length} unserved orders`}?`,
        description:
          `${unserved.map((o) => o.order_number).join(', ')} ${unserved.length === 1 ? 'has' : 'have'} not been served yet. ` +
          'Cancel: the kitchen stops and the cancellation is recorded on the order. ' +
          'Keep: the kitchen keeps preparing and the order stays open.',
        confirmText: unserved.length === 1 ? 'Cancel order' : 'Cancel orders',
        cancelText: 'Keep orders',
        tone: 'destructive',
      });
    }
    try {
      await freeTable(restaurantId, table.tableId, { cancelOrders });
      fetchTables();
    } catch (err) {
      await alertDialog({
        title: 'Could not free the table',
        description: errText(err),
        tone: 'destructive',
      });
    }
  };

  const closeBookingModal = () => {
    setBookingModal({ show: false, table: null });
    setBookingType('now');
    setBookingMode('SINGLE');
    setSelectedTablesToJoin([]);
    setSeatsNeeded('');
    setOverrideStaffId('');
    setOverrideReason('');
    setReservationTime('');
    setGuestName('');
    setGuestPhone('');
    setPax('');
  };

  const getStatusColor = (status) => {
    const colors = {
      'AVAILABLE': 'bg-green-100 text-green-800 border-green-300',
      'RESERVED': 'bg-blue-100 text-blue-800 border-blue-300',
      'OCCUPIED': 'bg-red-100 text-red-800 border-red-300',
      'PARTIAL': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const canBookTable = (table) => table.status === 'AVAILABLE' || table.status === 'PARTIAL';

  const filteredTables = useMemo(
    () => tables.filter((t) => !minCapacity || (t.capacity || 0) >= Number(minCapacity)),
    [tables, minCapacity],
  );

  /* READY → served: the guest has the plate. Same transition the POS floor
     presses; the server stores the terminal DELIVERED, cascades the lines,
     and pushes the update to the kitchen board and the guest's phone. */
  const markOrderServed = async (o, table) => {
    try {
      await apiClient.patch(`/users/orders/${o.id}/status/`, { status: 'served' });
      openTableDetail(table);
      fetchTables({ silent: true });
    } catch (e) {
      await alertDialog({
        title: 'Could not mark served',
        description: e?.response?.data?.error || 'The order did not update — try again.',
      });
    }
  };

  /* ONE dish at the pass → served, while the rest still cook. The server
     stamps the line's served_at and derives the order's status from its
     lines (the last dish served flips the order to SERVED — bill open). */
  const markItemServed = async (it, table) => {
    try {
      await apiClient.patch(`/users/order-items/${it.id}/status/`, { status: 'served' });
      openTableDetail(table);
      fetchTables({ silent: true });
    } catch (e) {
      await alertDialog({
        title: 'Could not mark the dish served',
        description: e?.response?.data?.error || 'The dish did not update — try again.',
      });
    }
  };

  // Clicking a card opens the table's order sheet: active orders grouped per
  // party (a shared table has several) + the table's full order history.
  const openTableDetail = async (table) => {
    setDetailModal({ table, loading: true, data: null, error: null });
    try {
      const { data } = await apiClient.get(
        `/users/restaurant/${restaurantId}/tables/${table.tableId}/orders/`,
      );
      setDetailModal((m) =>
        m && m.table.tableId === table.tableId
          ? { ...m, loading: false, data: data?.data || null }
          : m,
      );
    } catch (e) {
      setDetailModal((m) =>
        m && m.table.tableId === table.tableId
          ? { ...m, loading: false, error: e?.response?.data?.error || 'Failed to load orders for this table.' }
          : m,
      );
    }
  };

  /* Who is this guest? An order or a sitting carries the CRM customer id when
     the phone matched a registered guest, sometimes just a phone, and for a
     walk-in taken by name alone, nothing — in which case the card says so
     rather than pretending to look someone up. */
  const openGuestCard = async ({ name, customer_id: customerId, customer_phone: phone }) => {
    const seq = ++guestSeq.current;
    const canLookup = !!(customerId || phone);
    setGuestCard({ name: name || 'Guest', phone: phone || '', loading: canLookup, profile: null, error: '' });
    if (!canLookup) return;
    try {
      const profile = customerId
        ? await crmService.profileById(customerId)
        : await crmService.profileByPhone(phone);
      if (seq === guestSeq.current) setGuestCard((g) => (g ? { ...g, loading: false, profile } : g));
    } catch (e) {
      if (seq === guestSeq.current) {
        setGuestCard((g) => (g
          ? { ...g, loading: false, error: e?.response?.data?.error || 'Could not load this guest.' }
          : g));
      }
    }
  };

  // The guest's name as a link into their card. `ref` is the order or sitting
  // it came from (its customer_id / customer_phone travel with the name).
  const guestLink = (ref) => {
    const label = ref.customer_name || 'Guest';
    if (!ref.customer_name && !ref.customer_id && !ref.customer_phone) return label;
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); openGuestCard({ name: label, ...ref }); }}
        className="inline-flex items-center gap-1 font-medium text-blue-700 hover:text-blue-900 hover:underline underline-offset-2"
        title="See this guest's details"
      >
        <User size={11} /> {label}
      </button>
    );
  };

  /* SERVED — bill open → the bill. The order is re-read from the server (the
     sheet's rows carry no booking advance) and the taxes are the ones
     Settings → Tax applies today. Nothing is settled until Mark paid. */
  const openBill = async (o) => {
    setBill({ order: o, loading: true, detail: null, taxes: [], error: '', paid: false });
    try {
      const [detail, taxes] = await Promise.all([
        orderService.getOrderDetail(o.id),
        apiClient.get(`/users/restaurant/${restaurantId}/taxes/applicable/`)
          .then(({ data }) => (data?.success && data.results) || [])
          .catch(() => []),
      ]);
      setBill((b) => (b && b.order.id === o.id
        ? { ...b, loading: false, detail, taxes, paid: detail.status === 'COMPLETED' }
        : b));
    } catch (e) {
      setBill((b) => (b && b.order.id === o.id
        ? { ...b, loading: false, error: e?.response?.data?.error || e.message || 'Could not load the bill.' }
        : b));
    }
  };

  /* Re-read the order after a reward lands on it: the deduction lines the
     modal (and the print) show come back from the server's order detail, so
     the screen shows what was recorded, not what was attempted. */
  const refreshBill = async () => {
    const id = bill?.detail?.id;
    if (!id) return;
    const detail = await orderService.getOrderDetail(id);
    setBill((b) => (b && b.detail?.id === id ? { ...b, detail } : b));
  };

  const billHtml = () => buildBillHtml({
    restaurantName: restaurant?.name,
    locationName: location?.name,
    locationPhone: location?.phone,
    tableCode: detailModal?.table?.tableCode,
    order: bill.detail,
    bill: buildBill(bill.detail, bill.taxes),
    money: formatCurrency,
    paid: bill.paid,
  });

  /* Straight to the printer through the BOXX Print Bridge when it is
     installed (the default printer); otherwise a pop-up carrying just the
     bill, printed with the browser's own dialog. */
  const printBill = async () => {
    if (!bill?.detail) return;
    const html = billHtml();
    try {
      if (await detectBridge()) {
        const lp = await listPrinters().catch(() => ({ default: '', printers: [] }));
        const printer = lp.default || lp.printers?.[0]?.name || '';
        if (printer) {
          const r = await printHtml(html, printer);
          if (r?.ok) return;
        }
      }
    } catch { /* the browser prints it instead */ }
    const w = window.open('', '_blank', 'width=420,height=640');
    if (!w) {
      await alertDialog({ title: 'Pop-up blocked', description: 'Allow pop-ups for this site to print the bill.' });
      return;
    }
    w.document.open(); w.document.write(html); w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch { /* closed already */ } }, 300);
  };

  /* Paid: COMPLETED on the server — the same step as Mark Paid on the Orders
     page — which spends the sitting's booking advance (PAID → APPLIED) and
     stamps every line served. The table is NOT freed: a party often lingers
     after paying, so that stays the explicit Free table. */
  const settleBill = async () => {
    if (!bill?.detail) return;
    setBill((b) => (b ? { ...b, settling: true } : b));
    try {
      await orderService.updateOrderStatus(bill.detail.id, 'completed');
      const detail = await orderService.getOrderDetail(bill.detail.id)
        .catch(() => ({ ...bill.detail, status: 'COMPLETED' }));
      setBill((b) => (b ? { ...b, detail, paid: true, settling: false } : b));
      if (detailModal?.table) openTableDetail(detailModal.table);
      fetchTables({ silent: true });
    } catch (e) {
      setBill((b) => (b ? { ...b, settling: false } : b));
      await alertDialog({
        title: 'Could not close the order',
        description: e?.response?.data?.error || e?.message || 'The order did not update — try again.',
      });
    }
  };

  // Group by floor → section for display.
  const groupedByFloor = useMemo(() => {
    const floors = new Map();
    tables.forEach((t) => {
      if (!floors.has(t.floorId)) {
        floors.set(t.floorId, { id: t.floorId, name: t.floorName, floorNumber: t.floorNumber, sections: new Map() });
      }
      const floor = floors.get(t.floorId);
      if (!floor.sections.has(t.sectionId)) {
        floor.sections.set(t.sectionId, { id: t.sectionId, name: t.sectionName, tables: [] });
      }
      floor.sections.get(t.sectionId).tables.push(t);
    });
    return Array.from(floors.values())
      .sort((a, b) => (a.floorNumber ?? 0) - (b.floorNumber ?? 0))
      .map((f) => ({ ...f, sections: Array.from(f.sections.values()) }));
  }, [tables]);

  const stats = useMemo(() => ({
    total: tables.length,
    seats: tables.reduce((sum, t) => sum + (t.capacity || 0), 0),
    available: tables.filter((t) => t.status === 'AVAILABLE').length,
    occupied: tables.filter((t) => t.status === 'OCCUPIED').length,
  }), [tables]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading tables...</div>
      </div>
    );
  }

  const renderTableCard = (table) => (
    <Card
      key={table.tableId}
      className={`p-4 border-2 ${getStatusColor(table.status)} hover:shadow-lg transition-shadow cursor-pointer`}
      onClick={() => openTableDetail(table)}
      title="View this table's orders"
    >
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="text-lg font-bold">{table.tableCode}</h4>
          <div className="flex items-center text-sm text-gray-600 mt-1">
            <Users size={14} className="mr-1" />
            <span>{table.capacity} seats</span>
          </div>
          {table.tableType && (
            <div className="text-xs text-gray-500 mt-0.5 capitalize">{table.tableType}</div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(table.status)}`}>
            {table.status}
          </span>
          {table.isShared && (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300">
              SHARED · {table.sessions.length} {table.sessions.length === 1 ? 'party' : 'parties'}
            </span>
          )}
        </div>
      </div>

      {table.notes && (
        <div className="mb-3 text-xs text-gray-600 italic">{table.notes}</div>
      )}

      {/* Who's seated (open QR/booking sessions) */}
      {table.sessions.length > 0 && (
        <div className="mb-3 space-y-0.5">
          {table.sessions.map((s) => (
            <div key={s.id} className="text-xs text-gray-600 flex items-center gap-1">
              <Users size={11} className="shrink-0" />
              <span className="truncate">
                {s.customer_name || 'Guest'}
                {s.pax ? ` · ${s.pax} pax` : ''}
                {s.booking_mode === 'SHARE' ? ' · sharing' : ''}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Live orders on this table: number, status, and each item's progress */}
      {table.orders.length > 0 && (
        <div className="mb-3 space-y-2">
          {table.orders.map((o) => (
            <div key={o.order_number} className="rounded-md bg-white/70 border border-gray-200 p-2">
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="font-semibold text-gray-800 font-mono">{o.order_number}</span>
                <span className={`px-1.5 py-0.5 rounded-full font-semibold ${ORDER_STATUS_COLORS[o.status] || 'bg-gray-100 text-gray-700'}`}>
                  {/* Guests are SERVED; "delivered" is the model's word, not the floor's. */}
                  {o.status === 'DELIVERED' ? 'SERVED' : o.status}
                </span>
              </div>
              {/* WHO placed it: the guest's own name (phone order) or the staff
                  member who keyed it in — placed_by from the server, with the
                  old customer_name kept as the fallback for older payloads. */}
              {(o.placed_by?.name || o.customer_name) && (
                <div className="text-[11px] text-gray-500 mt-0.5">
                  👤 {o.placed_by?.kind === 'staff'
                    ? `Taken by ${o.placed_by.name}`
                    : `Ordered by ${o.placed_by?.name || o.customer_name}`}
                </div>
              )}
              {/* The guest's note to the kitchen, read off the card rather than off the ticket. */}
              {o.notes && (
                <div className="text-[11px] italic text-amber-700 mt-0.5">📝 {o.notes}</div>
              )}
              <div className="mt-1 space-y-0.5">
                {(o.items || []).map((it, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between gap-2 text-[11px]">
                      <span className="text-gray-700 min-w-0 truncate">
                        {it.name}{it.quantity > 1 ? ` ×${it.quantity}` : ''}
                      </span>
                      <span className={`shrink-0 font-medium ${ITEM_STATUS_COLORS[it.status] || 'text-gray-500'}`}>
                        {it.status}
                      </span>
                    </div>
                    {/* Ordered → served, per line: two dishes on one order were
                        ordered separately, so each carries its own clock. */}
                    {itemClockLine(it) && (
                      <div className="pl-3 text-[10px] tabular-nums text-gray-400">
                        🕐 {itemClockLine(it)}
                      </div>
                    )}
                    {it.notes && (
                      <div className="pl-3 text-[10px] italic text-amber-700">📝 {it.notes}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {canBookTable(table) && (
          <Button
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            size="sm"
            onClick={(e) => { e.stopPropagation(); setBookingModal({ show: true, table }); }}
          >
            Book Table
          </Button>
        )}
        {table.status === 'OCCUPIED' && (
          <Button
            variant="outline"
            className="w-full border-red-300 text-red-700 hover:bg-red-50"
            size="sm"
            onClick={(e) => { e.stopPropagation(); handleFreeTable(table); }}
          >
            Free Table
          </Button>
        )}
      </div>
    </Card>
  );

  return (
    <div className="p-6 w-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">Tables Management</h1>
        <p className="text-gray-600 mt-1 flex items-center gap-1">
          <MapPin size={16} />
          {location?.name || `Location ${locationId || ''}`}
        </p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-sm text-gray-600">Total Tables</div>
          <div className="text-2xl font-bold">{stats.total}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-gray-600">Total Seats</div>
          <div className="text-2xl font-bold">{stats.seats}</div>
        </Card>
        <Card className="p-4 bg-green-50">
          <div className="text-sm text-green-800">Available</div>
          <div className="text-2xl font-bold text-green-600">{stats.available}</div>
        </Card>
        <Card className="p-4 bg-red-50">
          <div className="text-sm text-red-800">Occupied</div>
          <div className="text-2xl font-bold text-red-600">{stats.occupied}</div>
        </Card>
      </div>

      {/* Table settings — reservation hold time */}
      <Card className="p-4 mb-6">
        <label className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
          <Clock size={13} /> Reservation hold time
        </label>
        {/* One aligned row: input · unit · presets · Save. The helper line
            sits UNDER the row, so nothing anchors the button to its baseline. */}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="number"
            value={reserveMinutes}
            onChange={(e) => { setReserveMinutes(e.target.value); setReserveSaved(false); }}
            min="5"
            max="1440"
            className="text-sm w-28"
          />
          <span className="text-sm text-gray-500">minutes</span>
          <div className="flex gap-1">
            {[30, 60, 90, 120].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => { setReserveMinutes(m); setReserveSaved(false); }}
                className={`text-xs px-2 py-1 rounded border ${
                  Number(reserveMinutes) === m
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {m === 60 ? '1 hr' : m === 90 ? '1.5 hr' : m === 120 ? '2 hr' : `${m}m`}
              </button>
            ))}
          </div>
          <Button size="sm" onClick={saveReserveMinutes} disabled={reserveSaving}>
            {reserveSaving ? 'Saving…' : reserveSaved ? 'Saved ✓' : 'Save'}
          </Button>
        </div>
        <p className="text-[11px] text-gray-400 mt-1">
          How long a guest's QR check-in reserves the table before it auto-frees.
        </p>
      </Card>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Tables grouped by floor → section */}
      {tables.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-gray-500">
            No tables set up for this location yet. Add them in Seating → Layout.
          </p>
        </Card>
      ) : (
        <div className="space-y-8">
          {groupedByFloor.map((floor) => (
            <div key={floor.id}>
              <div className="flex items-center gap-2 mb-3">
                <LayoutGrid size={18} className="text-gray-500" />
                <h2 className="text-xl font-semibold text-gray-900">{floor.name}</h2>
                <span className="text-sm text-gray-500">
                  ({floor.sections.reduce((n, s) => n + s.tables.length, 0)} tables)
                </span>
              </div>
              <div className="space-y-5">
                {floor.sections.map((section) => (
                  <div key={section.id}>
                    <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-2">
                      {section.name} · {section.tables.length} {section.tables.length === 1 ? 'table' : 'tables'}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {section.tables.map(renderTableCard)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table orders modal — active orders per party + past history */}
      {detailModal && (() => {
        const d = detailModal.data;
        const sessions = d?.sessions || [];
        const activeOrders = d?.active_orders || [];
        const history = d?.history || [];
        // Group active orders under the party (session) they belong to;
        // orders without a matching open session fall into one extra group.
        const bySession = new Map(sessions.map((s) => [s.id, []]));
        const unassigned = [];
        activeOrders.forEach((o) => {
          if (o.session_id && bySession.has(o.session_id)) bySession.get(o.session_id).push(o);
          else unassigned.push(o);
        });

        const renderOrder = (o, muted = false) => (
          <div
            key={o.id}
            className={`rounded-lg border p-3 ${muted ? 'border-gray-200 bg-gray-50' : 'border-blue-200 bg-blue-50/40'}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono font-semibold text-sm text-gray-900">{o.order_number}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                o.voided ? 'bg-gray-200 text-gray-600'
                  : ORDER_STATUS_COLORS[o.status]
                  || (o.status === 'CANCELLED' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700')
              }`}>
                {o.voided ? 'VOIDED' : o.status === 'DELIVERED' ? 'SERVED — bill open' : o.status}
              </span>
            </div>
            {/* Food at the pass: the floor's move, made right on the card. */}
            {!muted && !o.voided && o.status === 'READY' && (
              <button
                type="button"
                onClick={() => markOrderServed(o, detailModal.table)}
                className="mt-2 w-full rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
              >
                ✅ Mark served — guest has it
              </button>
            )}
            {/* Served but unpaid — in history too, since a served order leaves
                the active list. Opens the bill: print it for the guest, then
                Mark paid closes the order. */}
            {!o.voided && o.status === 'DELIVERED' && (
              <button
                type="button"
                onClick={() => openBill(o)}
                className="mt-2 w-full rounded-md bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-800 inline-flex items-center justify-center gap-1.5"
              >
                <Receipt size={13} /> Generate bill — close order
              </button>
            )}
            <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
              <span>
                {/* Staff-keyed orders name the staff member; the guest it was
                    taken FOR still gets their link when the order knows them. */}
                {o.placed_by?.kind === 'staff'
                  ? <>Taken by {o.placed_by.name}{(o.customer_name || o.customer_id) ? <> · for {guestLink(o)}</> : null}</>
                  : guestLink({ ...o, customer_name: o.placed_by?.name || o.customer_name })}
                {o.party_size ? ` · ${o.party_size} pax` : ''}
              </span>
              <span>{o.created_at ? fmtDateTime(o.created_at) : ''}</span>
            </div>
            <div className="mt-2 space-y-1">
              {(o.items || []).map((it, i) => (
                <div key={i} className="text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-gray-800 min-w-0 truncate">
                      {it.name}{it.quantity > 1 ? ` ×${it.quantity}` : ''}
                    </span>
                    <span className="shrink-0 flex items-center gap-2">
                      <span className={`font-medium ${ITEM_STATUS_COLORS[it.status] || 'text-gray-500'}`}>
                        {muted ? '' : it.status}
                      </span>
                      {/* THIS dish at the pass → served, while the rest still
                          cook — the runner takes one plate, not the order. */}
                      {!muted && !o.voided && it.status === 'READY' && it.id && (
                        <button
                          type="button"
                          onClick={() => markItemServed(it, detailModal.table)}
                          className="rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold text-white hover:bg-emerald-700"
                        >
                          ✓ Serve
                        </button>
                      )}
                      <span className="text-gray-600">{Number(it.total_price || 0).toFixed(2)}</span>
                    </span>
                  </div>
                  {/* Ordered → served per line — the whole point when two dishes
                      on one order were ordered at different times. */}
                  {itemClockLine(it) && (
                    <div className="text-[10px] tabular-nums text-gray-400 pl-2">
                      🕐 {itemClockLine(it)}
                    </div>
                  )}
                  {(it.addons || []).length > 0 && (
                    <div className="text-[11px] text-gray-500 pl-2">
                      + {it.addons.map((a) => (a.qty > 1 ? `${a.qty}× ${a.name}` : a.name)).join(', ')}
                    </div>
                  )}
                  {(it.complimentary || []).length > 0 && (
                    <div className="text-[11px] text-emerald-700 pl-2">
                      Comes with {it.complimentary.map((c) => c.name).join(', ')}
                    </div>
                  )}
                  {it.notes && (
                    <div className="text-[11px] text-gray-400 italic pl-2">{it.notes}</div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between text-xs">
              <span className="text-gray-500">Total</span>
              <span className="font-semibold text-gray-900">{Number(o.total_amount || 0).toFixed(2)}</span>
            </div>
          </div>
        );

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {detailModal.table.tableCode} — Orders
                  </h3>
                  <p className="text-sm text-gray-500">
                    {detailModal.table.floorName} · {detailModal.table.sectionName} ·{' '}
                    {detailModal.table.capacity} seats
                    {d?.is_shared && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300">
                        SHARED · {sessions.length} parties
                      </span>
                    )}
                  </p>
                </div>
                <button onClick={() => setDetailModal(null)} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>

              {detailModal.loading ? (
                <div className="py-12 text-center text-gray-500">Loading orders…</div>
              ) : detailModal.error ? (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                  {detailModal.error}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Active orders, one block per seated party */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Active orders ({activeOrders.length})
                    </h4>
                    {activeOrders.length === 0 ? (
                      <p className="text-sm text-gray-400">No active orders on this table.</p>
                    ) : (
                      <div className="space-y-4">
                        {sessions.map((s) => (
                          <div key={s.id}>
                            <div className="text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1">
                              <Users size={12} />
                              {guestLink(s)}
                              {s.pax ? ` · ${s.pax} pax` : ''}
                              {s.booking_mode === 'SHARE' ? ' · sharing' : ''}
                            </div>
                            {(bySession.get(s.id) || []).length === 0 ? (
                              <p className="text-xs text-gray-400 pl-4">No orders yet from this party.</p>
                            ) : (
                              <div className="space-y-2">{bySession.get(s.id).map((o) => renderOrder(o))}</div>
                            )}
                          </div>
                        ))}
                        {unassigned.length > 0 && (
                          <div>
                            {sessions.length > 0 && (
                              <div className="text-xs font-medium text-gray-600 mb-1.5">Other active orders</div>
                            )}
                            <div className="space-y-2">{unassigned.map((o) => renderOrder(o))}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Past orders on this table */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Order history ({history.length})
                    </h4>
                    {history.length === 0 ? (
                      <p className="text-sm text-gray-400">No past orders on this table yet.</p>
                    ) : (
                      <div className="space-y-2">{history.map((o) => renderOrder(o, true))}</div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </div>
        );
      })()}

      {/* The bill for one served order — above the sheet (z-70), so closing
          it lands back on the table's orders. */}
      {bill && (() => {
        const d = bill.detail;
        const b = d ? buildBill(d, bill.taxes) : null;
        return (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget) setBill(null); }}
          >
            <Card className="w-full max-w-md p-5 max-h-[88vh] overflow-y-auto">
              <div className="flex justify-between items-start gap-3 mb-3">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Receipt size={18} className="text-teal-700" /> Bill · {bill.order.order_number}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Table {detailModal?.table?.tableCode || d?.table_code || ''}
                    {d?.party_size ? ` · ${d.party_size} pax` : ''}
                    {d?.customer_name ? ` · ${d.customer_name}` : ''}
                  </p>
                </div>
                <button type="button" onClick={() => setBill(null)} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                  <X size={22} />
                </button>
              </div>

              {bill.loading ? (
                <p className="py-6 text-center text-sm text-gray-500">Working out the bill…</p>
              ) : bill.error ? (
                <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">{bill.error}</div>
              ) : (
                <>
                  <div className="space-y-1 text-sm text-gray-800">
                    {b.items.map((it, i) => (
                      <div key={i} className="flex justify-between gap-3">
                        <span className="min-w-0">
                          {it.qty} × {it.name}
                          {it.addons.length > 0 && (
                            <span className="text-gray-500 text-xs"> + {it.addons.join(', ')}</span>
                          )}
                        </span>
                        <span className="shrink-0">{formatCurrency(it.total)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-2 border-t border-dashed border-gray-300 space-y-1 text-sm text-gray-800">
                    <div className="flex justify-between"><span>Subtotal</span><span>{formatCurrency(b.subtotal)}</span></div>
                    {b.taxLines.map((t) => (
                      <div key={t.id} className="flex justify-between"><span>{t.label}</span><span>{formatCurrency(t.amount)}</span></div>
                    ))}
                    {b.advance > 0 && (
                      <div className="flex justify-between text-emerald-700">
                        <span>Booking advance{b.advanceStatus === 'APPLIED' ? ' (adjusted)' : ''}</span>
                        <span>− {formatCurrency(b.advance)}</span>
                      </div>
                    )}
                    {b.rewards.vouchers.filter((v) => v.value > 0).map((v) => (
                      <div key={v.code} className="flex justify-between text-teal-700">
                        <span className="min-w-0 truncate">Voucher · {v.title}</span>
                        <span className="shrink-0">− {formatCurrency(v.value)}</span>
                      </div>
                    ))}
                    {b.rewards.pointsValue > 0 && (
                      <div className="flex justify-between text-teal-700">
                        <span>Points redeemed</span>
                        <span>− {formatCurrency(b.rewards.pointsValue)}</span>
                      </div>
                    )}
                    {b.rewards.giftCardPaid > 0 && (
                      <div className="flex justify-between text-indigo-700">
                        <span>Gift card</span>
                        <span>− {formatCurrency(b.rewards.giftCardPaid)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold text-gray-900 border-t border-gray-900 pt-2 mt-1">
                      <span>{bill.paid ? 'Paid' : 'Amount due'}</span><span>{formatCurrency(b.due)}</span>
                    </div>
                  </div>
                  {!bill.paid && (
                    <BillRewardsPanel
                      orderId={d.id}
                      customerPhone={d.customer_phone}
                      grossTotal={b.grossTotal}
                      due={b.due}
                      locationId={locationId}
                      money={formatCurrency}
                      onApplied={refreshBill}
                    />
                  )}
                  {bill.paid && (
                    <p className="mt-3 text-center text-sm font-bold tracking-wide text-emerald-700">PAID — ORDER CLOSED</p>
                  )}
                  <div className="mt-4 flex gap-2">
                    <Button type="button" variant="outline" className="flex-1" onClick={printBill}>
                      <Printer size={14} className="mr-1.5" /> Print bill
                    </Button>
                    {!bill.paid && (
                      <Button type="button" className="flex-[2] bg-emerald-600 hover:bg-emerald-700 text-white"
                        disabled={!!bill.settling} onClick={settleBill}>
                        <CheckCircle size={14} className="mr-1.5" /> {bill.settling ? 'Closing…' : 'Mark paid & close order'}
                      </Button>
                    )}
                  </div>
                  {!bill.paid && (
                    <p className="mt-2 text-xs text-gray-400">
                      Print first if the guest wants to see it. Mark paid closes the order and spends
                      any booking advance; the table stays seated until you free it.
                    </p>
                  )}
                </>
              )}
            </Card>
          </div>
        );
      })()}

      {/* Guest card — who the name on the order sheet is. Sits above the
          sheet (z-60) so the sheet stays where it was when the card closes. */}
      {guestCard && (() => {
        const p = guestCard.profile;
        const c = p?.customer;
        const st = p?.stats;
        const money = (n) => formatCurrency(Number(n) || 0);
        const day = (iso) => (iso ? formatDate(iso) : '—');
        const email = c?.email && !c.email_is_placeholder ? c.email : '';
        const phone = c?.phone || guestCard.phone;
        const noShows = c?.no_show_count || 0;
        return (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget) setGuestCard(null); }}
          >
            <Card className="w-full max-w-md p-5 max-h-[85vh] overflow-y-auto">
              <div className="flex justify-between items-start gap-3 mb-3">
                <div className="min-w-0">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <User size={18} className="text-blue-700 shrink-0" />
                    <span className="truncate">{c?.full_name || guestCard.name}</span>
                  </h3>
                  {c && (
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>{c.is_active ? 'Registered guest' : 'Inactive account'}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setGuestCard(null)}
                  className="text-gray-400 hover:text-gray-600 shrink-0"
                  aria-label="Close"
                >
                  <X size={22} />
                </button>
              </div>

              {guestCard.loading ? (
                <p className="py-6 text-center text-sm text-gray-500">Looking up this guest…</p>
              ) : guestCard.error ? (
                <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
                  {guestCard.error}
                </div>
              ) : !p ? (
                <div className="space-y-2 text-sm">
                  {phone ? (
                    <p className="flex items-center gap-2 text-gray-800">
                      <Phone size={14} className="text-gray-400" />
                      <a href={`tel:${phone}`} className="hover:underline">{phone}</a>
                    </p>
                  ) : null}
                  <p className="text-gray-500">
                    Not a registered guest — only the name was taken with the
                    {phone ? ' booking, and the number is not in the customer register yet.' : ' order, so there is no phone number on file.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Contact — the reason the card exists. */}
                  <div className="space-y-1.5 text-sm">
                    <p className="flex items-center gap-2 text-gray-800">
                      <Phone size={14} className="text-gray-400" />
                      {phone ? <a href={`tel:${phone}`} className="font-medium hover:underline">{phone}</a> : '—'}
                    </p>
                    <p className="flex items-center gap-2 text-gray-800">
                      <Mail size={14} className="text-gray-400" />
                      {email ? <a href={`mailto:${email}`} className="hover:underline truncate">{email}</a>
                        : <span className="text-gray-400">no email</span>}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {[
                      ['Visits', st?.visit_count ?? 0],
                      ['Total spend', money(st?.total_spend)],
                      ['Avg order', money(st?.avg_order_value)],
                      ['Last visit', day(st?.last_visit)],
                      ['Usual party', st?.avg_party_size ? st.avg_party_size.toFixed(1) : '—'],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-lg bg-gray-50 p-2.5">
                        <div className="text-[11px] text-gray-500">{label}</div>
                        <div className="text-base font-semibold text-gray-900 truncate">{value}</div>
                      </div>
                    ))}
                    <div className={`rounded-lg p-2.5 ${noShows > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                      <div className={`text-[11px] ${noShows > 0 ? 'text-red-600' : 'text-gray-500'}`}>No-shows</div>
                      <div className={`text-base font-semibold ${noShows > 0 ? 'text-red-700' : 'text-gray-900'}`}>{noShows}</div>
                    </div>
                  </div>

                  {(st?.advance_available || 0) > 0 && (
                    <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                      Booking advance held: <strong>{money(st.advance_available)}</strong>
                      <span className="text-emerald-600"> — deducted from their next bill</span>
                    </div>
                  )}

                  {(p.favourites || []).length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Favourite dishes</div>
                      <div className="flex flex-wrap gap-1.5">
                        {p.favourites.slice(0, 4).map((f) => (
                          <span key={f.item} className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-xs">
                            {f.item} ×{f.quantity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate(`/crm?customer=${c.id}&from=tables`)}
                  >
                    <ExternalLink size={14} className="mr-1.5" /> Open full CRM profile
                  </Button>
                </div>
              )}
            </Card>
          </div>
        );
      })()}

      {/* Booking Modal */}
      {bookingModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">
                Book {bookingModal.table?.tableCode}
              </h3>
              <button onClick={closeBookingModal} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="mb-6 p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  {bookingModal.table?.floorName} · {bookingModal.table?.sectionName}
                </span>
                <span className="font-semibold">{bookingModal.table?.capacity} seats</span>
              </div>
            </div>

            {/* Booking Type */}
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-3 text-gray-700">Booking Type</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setBookingType('now')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    bookingType === 'now' ? 'border-blue-600 bg-blue-50 shadow-md' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <CheckCircle size={24} className={bookingType === 'now' ? 'text-blue-600 mb-2' : 'text-gray-400 mb-2'} />
                    <span className={`font-semibold ${bookingType === 'now' ? 'text-blue-700' : 'text-gray-700'}`}>Immediate</span>
                    <span className="text-xs text-gray-500 mt-1">Seat guests now</span>
                  </div>
                </button>
                <button
                  onClick={() => setBookingType('later')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    bookingType === 'later' ? 'border-purple-600 bg-purple-50 shadow-md' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <Clock size={24} className={bookingType === 'later' ? 'text-purple-600 mb-2' : 'text-gray-400 mb-2'} />
                    <span className={`font-semibold ${bookingType === 'later' ? 'text-purple-700' : 'text-gray-700'}`}>Appointment</span>
                    <span className="text-xs text-gray-500 mt-1">Reserve for later</span>
                  </div>
                </button>
              </div>
            </div>

            {bookingType === 'later' && (
              <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <label className="block text-sm font-semibold mb-3 text-purple-900">
                  <Clock size={16} className="inline mr-2" />
                  Appointment Date & Time
                </label>
                {/* Date and clock as separate controls — the clock is the same
                    three boxes as everywhere else. `reservationTime` stays one
                    "YYYY-MM-DDTHH:MM" string, so the booking call is unchanged.
                    The date cannot be in the past: this books a table. */}
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    type="date"
                    value={reservationTime.slice(0, 10)}
                    min={new Date().toLocaleDateString('en-CA')}
                    onChange={(e) => setReservationTime(
                      `${e.target.value}T${reservationTime.slice(11) || '19:00'}`)}
                    className="text-base p-3 border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                  />
                  <TimePicker
                    value={reservationTime.slice(11)}
                    step={15}
                    onChange={(v) => setReservationTime(
                      `${reservationTime.slice(0, 10) || new Date().toLocaleDateString('en-CA')}T${v}`)}
                  />
                </div>
                {reservationTime && (
                  <div className="mt-2 text-sm text-purple-700">
                    <strong>Selected:</strong> {fmtDateTime(reservationTime)}
                  </div>
                )}
              </div>
            )}

            {/* Number of Guests */}
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                <Users size={16} className="inline mr-2" />
                Number of Guests
              </label>
              <Input
                type="number"
                placeholder="How many guests?"
                value={pax}
                onChange={(e) => {
                  setPax(e.target.value);
                  if (parseInt(e.target.value) <= bookingModal.table?.capacity) {
                    setBookingMode('SINGLE');
                  }
                }}
                min="1"
                className="w-full text-base p-3"
              />
              {pax && parseInt(pax) > bookingModal.table?.capacity && (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 text-sm font-medium mb-2">
                    ⚠️ Exceeds table capacity ({bookingModal.table?.capacity} seats)
                  </p>
                  <p className="text-amber-700 text-xs">Select a booking mode below to accommodate {pax} guests.</p>
                </div>
              )}
            </div>

            {/* Booking Mode Selection */}
            {pax && parseInt(pax) > bookingModal.table?.capacity && (
              <div className="mb-6 p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200">
                <label className="block text-sm font-bold mb-3 text-gray-900">Select Booking Mode</label>
                <div className="grid grid-cols-1 gap-3">
                  <button type="button" onClick={() => setBookingMode('JOIN')}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${bookingMode === 'JOIN' ? 'border-blue-600 bg-blue-100 shadow-md' : 'border-gray-300 bg-white hover:border-blue-400'}`}>
                    <div className="font-semibold text-blue-700">🔗 Join Tables</div>
                    <div className="text-xs text-gray-600 mt-1">Combine multiple tables for larger parties</div>
                  </button>
                  <button type="button" onClick={() => setBookingMode('SHARE')}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${bookingMode === 'SHARE' ? 'border-green-600 bg-green-100 shadow-md' : 'border-gray-300 bg-white hover:border-green-400'}`}>
                    <div className="font-semibold text-green-700">👥 Share Table</div>
                    <div className="text-xs text-gray-600 mt-1">Share this table with another party</div>
                  </button>
                  <button type="button" onClick={() => setBookingMode('OVERRIDE')}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${bookingMode === 'OVERRIDE' ? 'border-red-600 bg-red-100 shadow-md' : 'border-gray-300 bg-white hover:border-red-400'}`}>
                    <div className="font-semibold text-red-700">⚡ Manager Override</div>
                    <div className="text-xs text-gray-600 mt-1">Requires manager approval to exceed capacity</div>
                  </button>
                </div>
              </div>
            )}

            {/* JOIN Mode - Table Selection */}
            {bookingMode === 'JOIN' && pax && parseInt(pax) > bookingModal.table?.capacity && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <label className="block text-sm font-bold mb-3 text-blue-900">🔗 Select Additional Tables to Join</label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {tables
                    .filter((t) => t.tableId !== bookingModal.table?.tableId && t.status === 'AVAILABLE')
                    .map((table) => (
                      <label key={table.tableId} className="flex items-center p-2 bg-white rounded hover:bg-blue-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedTablesToJoin.includes(table.tableId)}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedTablesToJoin([...selectedTablesToJoin, table.tableId]);
                            else setSelectedTablesToJoin(selectedTablesToJoin.filter((id) => id !== table.tableId));
                          }}
                          className="mr-3"
                        />
                        <span className="font-medium">{table.tableCode}</span>
                        <span className="ml-auto text-sm text-gray-600">{table.capacity} seats</span>
                      </label>
                    ))}
                </div>
                {selectedTablesToJoin.length > 0 && (
                  <div className="mt-3 p-2 bg-blue-100 rounded text-sm">
                    <strong>Total Capacity:</strong>{' '}
                    {bookingModal.table?.capacity + tables.filter((t) => selectedTablesToJoin.includes(t.tableId)).reduce((sum, t) => sum + t.capacity, 0)} seats
                  </div>
                )}
              </div>
            )}

            {/* SHARE Mode */}
            {bookingMode === 'SHARE' && pax && parseInt(pax) > bookingModal.table?.capacity && (
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <label className="block text-sm font-bold mb-3 text-green-900">👥 How many seats needed?</label>
                <Input type="number" placeholder="Number of seats to allocate" value={seatsNeeded}
                  onChange={(e) => setSeatsNeeded(e.target.value)} min="1" max={bookingModal.table?.capacity}
                  className="w-full border-green-300 focus:border-green-500 focus:ring-green-500" />
              </div>
            )}

            {/* OVERRIDE Mode */}
            {bookingMode === 'OVERRIDE' && pax && parseInt(pax) > bookingModal.table?.capacity && (
              <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
                <label className="block text-sm font-bold mb-3 text-red-900">⚡ Manager Override Required</label>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-red-800">Manager/Staff ID</label>
                    <Input type="number" placeholder="Enter staff ID" value={overrideStaffId}
                      onChange={(e) => setOverrideStaffId(e.target.value)}
                      className="w-full border-red-300 focus:border-red-500 focus:ring-red-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-red-800">Reason for Override</label>
                    <textarea placeholder="Explain why capacity override is needed..." value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)} rows="2"
                      className="w-full px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm" />
                  </div>
                </div>
              </div>
            )}

            {/* Guest Details */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-3">Guest Details (Optional)</h4>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-gray-700">Guest Name</label>
                <Input type="text" placeholder="Enter guest name" value={guestName}
                  onChange={(e) => setGuestName(e.target.value)} className="w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Guest Phone</label>
                <Input type="tel" placeholder="Enter phone number" value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)} className="w-full" />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 py-3" onClick={closeBookingModal}>Cancel</Button>
              <Button
                className={`flex-1 py-3 text-white font-semibold ${bookingType === 'now' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                onClick={handleBookTable}
                disabled={
                  !pax ||
                  (bookingType === 'later' && !reservationTime) ||
                  (bookingMode === 'JOIN' && selectedTablesToJoin.length === 0 && parseInt(pax) > bookingModal.table?.capacity) ||
                  (bookingMode === 'SHARE' && !seatsNeeded && parseInt(pax) > bookingModal.table?.capacity) ||
                  (bookingMode === 'OVERRIDE' && (!overrideStaffId || !overrideReason) && parseInt(pax) > bookingModal.table?.capacity)
                }
              >
                {bookingType === 'now' ? 'Book Immediately' : 'Schedule Appointment'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TablesViewPage;
